import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWallet } from '../context/WalletContext';
import { cryptoAPI } from '../api/endpoints';
import { formatXPUNT, formatDate, formatTimeAgo } from '../utils/helpers';
import { getExchangeRate } from '../utils/exchangeRate';
import { CURRENCIES } from '../utils/constants';
import { 
  FaArrowLeft, FaBitcoin, FaCopy, FaCheck,
  FaClock, FaCheckCircle, FaTimesCircle,
  FaSpinner, FaWallet, FaInfoCircle,
  FaCalendar, FaMoneyBillWave, FaShieldAlt,
  FaExclamationTriangle, FaFileInvoice
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import './CryptoDeposit.css';

const CryptoDeposit = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const { balance, fetchBalance, fetchTransactions } = useWallet();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deposits, setDeposits] = useState([]);
  const [cryptoAddress, setCryptoAddress] = useState('');
  const [walletSettings, setWalletSettings] = useState({
    address: '',
    coin: 'USDC',
    chain: 'ETH',
    network: 'Ethereum (ERC-20)',
  });
  const [exchangeRate, setExchangeRate] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    amount: '',
    tx_id: '',
    from_address: '',
    deposit_date: '',
    coin: 'USDC',
    chain: 'ETH',
  });

  // Get user's fixed currency
  const userCurrency = user?.local_currency || 'NGN';
  const currencySymbol = user?.currency_symbol || CURRENCIES[userCurrency]?.symbol || '₦';

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchData();
  }, [isAuthenticated, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch exchange rate
      const rate = await getExchangeRate(userCurrency);
      setExchangeRate(rate);

      // Fetch wallet settings from admin
      const walletRes = await cryptoAPI.getWalletSettings();
      if (walletRes.data && walletRes.data.success) {
        const settings = walletRes.data;
        setWalletSettings({
          address: settings.address || '',
          coin: settings.coin || 'USDC',
          chain: settings.chain || 'ETH',
          network: settings.network || 'Ethereum (ERC-20)',
        });
        setCryptoAddress(settings.address || '');
      }

      // Fetch deposit history
      const historyRes = await cryptoAPI.getHistory();
      if (historyRes.data && historyRes.data.success) {
        setDeposits(historyRes.data.data || []);
      } else {
        setDeposits([]);
      }
    } catch (error) {
      console.error('Failed to fetch crypto data:', error);
      toast.error('Failed to load crypto data');
      setDeposits([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const amount = parseFloat(formData.amount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid amount in USDC');
      return;
    }
    if (!formData.tx_id || formData.tx_id.length < 10) {
      toast.error('Please enter a valid transaction ID');
      return;
    }
    if (!formData.from_address || formData.from_address.length < 10) {
      toast.error('Please enter a valid from address');
      return;
    }
    if (!formData.deposit_date) {
      toast.error('Please select deposit date');
      return;
    }

    setSubmitting(true);
    try {
      // Submit deposit request - NO FEES, 1:1 conversion
      const response = await cryptoAPI.processDeposit({
        amount: amount, // Full amount - no fees
        transactionId: formData.tx_id,
        fromAddress: formData.from_address,
        depositDate: formData.deposit_date,
        coin: formData.coin,
        chain: formData.chain,
      });

      console.log('Deposit response:', response.data);

      if (response.data && response.data.success) {
        toast.success(response.data.message || 'Crypto deposit submitted for approval!');
        setFormData({
          amount: '',
          tx_id: '',
          from_address: '',
          deposit_date: '',
          coin: 'USDC',
          chain: 'ETH',
        });
        await fetchData();
        await fetchBalance();
      } else {
        toast.error(response.data?.message || 'Failed to submit deposit');
      }
    } catch (error) {
      console.error('Submit deposit error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit deposit';
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyAddress = () => {
    if (cryptoAddress) {
      navigator.clipboard.writeText(cryptoAddress);
      toast.success('Address copied!');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: <FaClock />, label: 'Pending', className: 'pending' },
      approved: { icon: <FaCheckCircle />, label: 'Approved', className: 'approved' },
      cancelled: { icon: <FaTimesCircle />, label: 'Cancelled', className: 'cancelled' },
    };
    return badges[status] || badges.pending;
  };

  if (loading) {
    return (
      <div className="crypto-deposit-loading">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Calculate XPUNT amount (USDC to XPUNT, 1:1 - NO FEES)
  const xpuntAmount = formData.amount ? parseFloat(formData.amount) : 0;
  const localAmount = xpuntAmount * (exchangeRate || 1);

  // Check if wallet address is set
  const isWalletConfigured = cryptoAddress && cryptoAddress.length > 10;

  return (
    <div className="crypto-deposit-page">
      <button onClick={() => navigate(-1)} className="back-button">
        <FaArrowLeft /> Back
      </button>

      <div className="crypto-deposit-header">
        <h1>
          <FaBitcoin className="header-icon" /> Crypto Deposit
        </h1>
        <p className="header-subtitle">
          Deposit {walletSettings.coin || 'USDC'} and get credited in $XPUNT (1 {walletSettings.coin || 'USDC'} = 1 $XPUNT - <strong>No Fees</strong>)
        </p>
      </div>

      <div className="crypto-deposit-grid">
        {/* Deposit Form */}
        <div className="deposit-form-card">
          <h2>Submit Deposit</h2>
          <p className="form-hint">
            Send {walletSettings.coin || 'USDC'} to the address below and submit the transaction details
          </p>

          {/* No Fee Banner */}
          <div className="no-fee-banner">
            <FaShieldAlt className="no-fee-icon" />
            <span><strong>0% Fee</strong> - You get exactly what you deposit</span>
          </div>

          {/* Wallet Address Display */}
          <div className="wallet-address-section">
            <div className="address-label">
              <FaWallet /> {walletSettings.coin || 'USDC'} Deposit Address ({walletSettings.chain || 'ETH'})
            </div>
            <div className="address-box">
              {isWalletConfigured ? (
                <>
                  <code>{cryptoAddress}</code>
                  <button onClick={handleCopyAddress} className="copy-address-btn">
                    <FaCopy />
                  </button>
                </>
              ) : (
                <div className="address-not-configured">
                  <FaExclamationTriangle className="warning-icon" />
                  <span>Wallet address not configured by admin. Please contact support.</span>
                </div>
              )}
            </div>
            {isWalletConfigured && (
              <small className="address-warning">
                ⚠️ Only send {walletSettings.coin || 'USDC'} on the {walletSettings.network || 'Ethereum (ERC-20)'} network
              </small>
            )}
          </div>

          {/* Powered by Trendly.ng */}
          <div className="payment-powered">
            <span className="powered-text">🔒 Powered by</span>
            <span className="powered-brand">Trendly.ng</span>
            <span className="powered-text">Payment</span>
          </div>

          <form onSubmit={handleSubmit} className="deposit-form">
            <div className="form-group">
              <label>{walletSettings.coin || 'USDC'} Amount *</label>
              <div className="input-wrapper">
                <span className="input-prefix">$</span>
                <input
                  type="number"
                  name="amount"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={handleFormChange}
                  min="0.01"
                  step="0.01"
                  required
                  disabled={!isWalletConfigured}
                />
              </div>
              {formData.amount && (
                <small className="conversion-hint">
                  You will receive: <strong>{formatXPUNT(xpuntAmount)}</strong> ($XPUNT)
                  {exchangeRate && (
                    <span> ≈ {currencySymbol}{localAmount.toFixed(2)} {userCurrency}</span>
                  )}
                  <span className="no-fee-hint">(No fees applied)</span>
                </small>
              )}
            </div>

            <div className="form-group">
              <label>Transaction ID (TX Hash) *</label>
              <input
                type="text"
                name="tx_id"
                placeholder="0x..."
                value={formData.tx_id}
                onChange={handleFormChange}
                required
                disabled={!isWalletConfigured}
              />
              <small className="field-hint">Must be unique. This will be verified.</small>
            </div>

            <div className="form-group">
              <label>From Address *</label>
              <input
                type="text"
                name="from_address"
                placeholder="0x..."
                value={formData.from_address}
                onChange={handleFormChange}
                required
                disabled={!isWalletConfigured}
              />
            </div>

            <div className="form-group">
              <label>Deposit Date *</label>
              <input
                type="datetime-local"
                name="deposit_date"
                value={formData.deposit_date}
                onChange={handleFormChange}
                required
                disabled={!isWalletConfigured}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Coin</label>
                <select
                  name="coin"
                  value={formData.coin}
                  onChange={handleFormChange}
                  disabled={!isWalletConfigured}
                >
                  <option value="USDC">USDC</option>
                  <option value="USDT">USDT</option>
                </select>
              </div>
              <div className="form-group">
                <label>Chain</label>
                <select
                  name="chain"
                  value={formData.chain}
                  onChange={handleFormChange}
                  disabled={!isWalletConfigured}
                >
                  <option value="ETH">Ethereum (ERC-20)</option>
                  <option value="BSC">BSC (BEP-20)</option>
                </select>
              </div>
            </div>

            <div className="form-info">
              <FaInfoCircle className="info-icon" />
              <span>
                1 {walletSettings.coin || 'USDC'} = 1 $XPUNT (1 $XPUNT = 1 USD) - <strong>No Fees</strong>
                {exchangeRate && ` = ${currencySymbol}${exchangeRate.toFixed(2)} ${userCurrency}`}
              </span>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="btn btn-primary btn-lg"
                disabled={submitting || !isWalletConfigured}
              >
                {submitting ? (
                  <LoadingSpinner size="small" />
                ) : (
                  <>
                    <FaFileInvoice /> Submit for Approval
                  </>
                )}
              </button>
            </div>

            {!isWalletConfigured && (
              <div className="wallet-not-configured-warning">
                <FaExclamationTriangle className="warning-icon" />
                <span>Wallet address not configured. Please contact support to make deposits.</span>
              </div>
            )}
          </form>
        </div>

        {/* Deposit History */}
        <div className="deposit-history-card">
          <h2>
            <FaClock /> Deposit History
          </h2>

          {deposits.length === 0 ? (
            <div className="empty-state">
              <p>No deposit history</p>
            </div>
          ) : (
            <div className="deposits-list">
              {deposits.map((deposit) => {
                const status = getStatusBadge(deposit.status);
                return (
                  <div key={deposit.id} className="deposit-item">
                    <div className="deposit-header">
                      <span className="deposit-amount">
                        {deposit.amount} {deposit.coin || 'USDC'}
                      </span>
                      <span className={`status-badge ${status.className}`}>
                        {status.icon} {status.label}
                      </span>
                    </div>
                    <div className="deposit-details">
                      <div className="detail-row">
                        <span>TX ID</span>
                        <span className="tx-id">{deposit.tx_id?.slice(0, 15)}...</span>
                      </div>
                      <div className="detail-row">
                        <span>From</span>
                        <span className="from-address">{deposit.from_address?.slice(0, 12)}...</span>
                      </div>
                      <div className="detail-row">
                        <span>Date</span>
                        <span>{formatDate(deposit.created_at)}</span>
                      </div>
                      {deposit.status === 'approved' && (
                        <div className="detail-row approved-row">
                          <span>XPUNT Credited</span>
                          <span className="xpunt-amount">
                            +{formatXPUNT(deposit.amount)} <span className="no-fee-tag">(No Fee)</span>
                          </span>
                        </div>
                      )}
                      {deposit.status === 'cancelled' && deposit.reason && (
                        <div className="detail-row cancelled-row">
                          <span>Reason</span>
                          <span className="cancelled-reason">{deposit.reason}</span>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Info Section */}
      <div className="crypto-info-section">
        <div className="info-card">
          <FaShieldAlt className="info-card-icon" />
          <h4>0% Fee</h4>
          <p>No fees on crypto deposits. You get exactly what you deposit.</p>
        </div>
        <div className="info-card">
          <FaClock className="info-card-icon" />
          <h4>Processing Time</h4>
          <p>Approved deposits are credited within 24 hours</p>
        </div>
        <div className="info-card">
          <FaMoneyBillWave className="info-card-icon" />
          <h4>1:1 Conversion</h4>
          <p>1 {walletSettings.coin || 'USDC'} = 1 $XPUNT (pegged to USD)</p>
        </div>
      </div>
    </div>
  );
};

export default CryptoDeposit;